import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { OperationsApi, OperationsDto } from '@buspro/web/apis';
import { injectQuery } from '@tanstack/angular-query-experimental';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzTableModule } from 'ng-zorro-antd/table';
import { MomentModule } from 'ngx-moment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-audit-reports-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NzButtonModule,
    NzIconModule,
    NzCardModule,
    NzSpinModule,
    NzStatisticModule,
    NzTableModule,
    NzInputModule,
    NzSelectModule,
    MomentModule,
  ],
  templateUrl: './audit-reports-page.component.html',
  styleUrls: ['./audit-reports-page.component.scss'],
})
export class AuditReportsPageComponent {
  private operationsApi = inject(OperationsApi);
  private router = inject(Router);

  /**
   * Open the comprehensive per-trip audit loss report.
   */
  viewReport(audit: OperationsDto) {
    this.router.navigate(['/audits', audit.id, 'report']);
  }

  searchTerm = signal('');
  sortField = signal<'auditCompletedAt' | 'auditDuration'>('auditCompletedAt');
  sortOrder = signal<'asc' | 'desc'>('desc');

  // Query for all completed audits
  completedAuditsQuery = injectQuery(() => ({
    queryKey: ['all-completed-audits'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetAllCompletedAudits()),
    refetchInterval: 60000, // Auto-refresh every minute
  }));

  /**
   * Get filtered audits based on search term
   */
  getFilteredAudits(): OperationsDto[] {
    const audits = this.completedAuditsQuery.data() || [];
    const search = this.searchTerm().toLowerCase();

    if (!search) {
      return this.getSortedAudits(audits);
    }

    const filtered = audits.filter(
      (audit) =>
        audit.auditNumber?.toLowerCase().includes(search) ||
        audit.routeName?.toLowerCase().includes(search) ||
        audit.busName?.toLowerCase().includes(search) ||
        audit.auditStartedByName?.toLowerCase().includes(search),
    );

    return this.getSortedAudits(filtered);
  }

  /**
   * Sort audits based on current sort settings
   */
  private getSortedAudits(audits: OperationsDto[]): OperationsDto[] {
    const sorted = [...audits];
    const field = this.sortField();
    const order = this.sortOrder();

    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (field === 'auditCompletedAt') {
        aVal = a.auditCompletedAt ? new Date(a.auditCompletedAt).getTime() : 0;
        bVal = b.auditCompletedAt ? new Date(b.auditCompletedAt).getTime() : 0;
      } else if (field === 'auditDuration') {
        aVal = a.auditDuration || 0;
        bVal = b.auditDuration || 0;
      }

      if (order === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    return sorted;
  }

  /**
   * Format duration from seconds to human-readable string
   */
  formatDuration(seconds: number | null | undefined): string {
    if (!seconds) {
      return 'N/A';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
  }

  /**
   * Get summary statistics from all audits
   */
  getStats() {
    const audits = this.completedAuditsQuery.data() || [];

    const totalAudits = audits.length;
    const totalDuration = audits.reduce((sum, audit) => sum + (audit.auditDuration || 0), 0);
    const avgDuration = totalAudits > 0 ? totalDuration / totalAudits : 0;

    // Get unique auditors
    const uniqueAuditors = new Set(audits.map((a) => a.auditStartedBy).filter((id) => id));

    // Get audits from today
    const today = moment().startOf('day');
    const auditedToday = audits.filter((a) => a.auditCompletedAt && moment(a.auditCompletedAt).isAfter(today));

    return {
      total: totalAudits,
      avgDuration,
      uniqueAuditors: uniqueAuditors.size,
      today: auditedToday.length,
    };
  }

  /**
   * Refresh audits list
   */
  refresh() {
    this.completedAuditsQuery.refetch();
  }
}
